import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Server, Laptop, Monitor, HardDrive, Database, UserCircle, Shield, Cpu, AlertTriangle, ChevronRight, Lock, Activity } from 'lucide-react';

const DataWorkflow = () => {
  // State to track data flow animation
  const [activeFlow, setActiveFlow] = useState<number>(0);

  // State for real-time stat updates
  const [stats, setStats] = useState({
    logsProcessed: 1457834,
    totalEndpointsCapacity: 2250,
    threatEvents: 17,
    logsPerSecond: 12743,
    diskUsage: 76.4,
    cpuLoad: 38.2
  });

  // Simulated data for node activity and status
  const nodes = {
    endpoints: [
      { id: 'server1', type: 'Server', ip: 'Hide', status: 'active', logCount: 9478 },
      { id: 'desktop1', type: 'Desktop', ip: 'Hide', status: 'active', logCount: 3215 },
      { id: 'laptop1', type: 'Laptop', ip: 'Hide', status: 'active', logCount: 2187 },
      { id: 'vm1', type: 'Virtual Machine', ip: 'Hide', status: 'active', logCount: 5632 }
    ],
    alertstrikeWorkers: [
      { id: 'master1', name: 'AlertStrike-Master-1', ip: '172.17.4.163', status: 'operational', load: 52 },
      { id: 'worker1', name: 'AlertStrike-Worker-1', ip: '172.17.4.164', status: 'operational', load: 42 },
      { id: 'worker2', name: 'AlertStrike-Worker-2', ip: '172.17.4.165', status: 'operational', load: 38 },
      { id: 'worker3', name: 'AlertStrike-Worker-3', ip: '172.17.4.166', status: 'high-load', load: 89 },
      { id: 'worker4', name: 'AlertStrike-Worker-4', ip: '172.17.4.167', status: 'operational', load: 41 },
      { id: 'worker5', name: 'AlertStrike-Worker-5', ip: '172.17.4.168', status: 'operational', load: 37 }
    ],
    master: {
      id: 'master1', 
      name: 'Z0RX AI Engine',
      ip: '172.17.4.163',
      status: 'operational',
      load: 42,
      dataRate: '142.7 MB/s'
    },
    indexers: [
      { id: 'indexer1', name: 'AlertStrike-Indexer-1', ip: '172.17.4.160', status: 'operational', indexRate: '45.2 K/s' },
      { id: 'indexer2', name: 'AlertStrike-Indexer-2', ip: '172.17.4.161', status: 'operational', indexRate: '38.9 K/s' },
      { id: 'indexer3', name: 'AlertStrike-Indexer-3', ip: '172.17.4.162', status: 'operational', indexRate: '42.1 K/s' },
    ],
    dashboard: {
      id: 'dashboard1',
      name: 'AlertStrike-Dashboard',
      ip: '172.17.4.175',
      status: 'operational',
      activeUsers: 24
    }
  };

  // Update stats periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prevStats => ({
        logsProcessed: prevStats.logsProcessed + Math.floor(Math.random() * 100),
        totalEndpointsCapacity: 2250,
        threatEvents: prevStats.threatEvents + (Math.random() > 0.95 ? 1 : 0),
        logsPerSecond: 12500 + Math.floor(Math.random() * 500),
        diskUsage: 76.0 + Math.random(),
        cpuLoad: 35 + Math.random() * 6
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Cycle through active data flow animations
  useEffect(() => {
    const flowInterval = setInterval(() => {
      setActiveFlow(prev => (prev + 1) % 12);
    }, 800);

    return () => clearInterval(flowInterval);
  }, []);

  // Determines which flow line should be active based on the current activeFlow state
  const isActiveFlow = (flowIndex: number) => {
    return activeFlow === flowIndex;
  };

  return (
    <div className="container mx-auto px-6 py-8">
      {/* HUD-Style Top Bar */}
      <div className="flex items-center justify-between mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex items-center space-x-4 px-4">
          <div className="text-red-500 font-mono text-sm">
            <span className="text-slate-400">SYS:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-sm">
            <span className="text-slate-400">LOG RATE:</span> {stats.logsPerSecond.toLocaleString()} PER SEC
          </div>
          <div className="text-red-500 font-mono text-sm">
            <span className="text-slate-400">THREATS:</span> {stats.threatEvents} DETECTED
          </div>
        </div>
        <div className="flex items-center">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-4" style={{ width: '100px' }}></div>
          <div className="text-red-400 font-mono text-xs px-4">
            <span className="text-slate-500">TIMESTAMP:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="mb-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center">
              <Shield className="h-8 w-8 mr-3 text-red-600" />
              <span>AlertStrike</span>
              <span className="text-red-500 ml-2">Data Workflow</span>
              <div className="ml-3 h-2 w-2 bg-red-600 rounded-full animate-pulse"></div>
            </h1>
            <p className="text-gray-400 max-w-3xl">
              Real-time visualization of security telemetry data flow from endpoints through AlertStrike processing 
              infrastructure to the security operations dashboard.
            </p>
          </div>

          {/* Stats Card */}
          <div className="bg-black/40 rounded-lg border border-red-900/30 p-4 backdrop-blur-sm">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-slate-400">Logs Processed</div>
                <div className="text-xl font-bold text-red-400">{stats.logsProcessed.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-400">Total Endpoints Capacity</div>
                <div className="text-xl font-bold text-green-400">{stats.totalEndpointsCapacity.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-400">Cluster Health</div>
                <div className="flex items-center justify-center">
                  <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
                  <span className="text-xl font-bold text-green-400">98%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Workflow Visualization */}
      <div className="bg-black/60 border border-slate-800 rounded-xl p-6 backdrop-blur-sm shadow-xl mb-10 relative overflow-hidden">
        {/* Security scan line effect */}
        <div className="absolute left-0 right-0 h-px bg-red-500/30 z-10" 
          style={{ 
            top: '50%',
            animation: 'scanHorizontal 20s infinite alternate linear',
            boxShadow: '0 0 10px rgba(239, 68, 68, 0.3), 0 0 20px rgba(239, 68, 68, 0.2)'
          }}>
        </div>

        {/* Grid background */}
        <div className="absolute inset-0 animated-grid opacity-20"></div>

        {/* Workflow Title */}
        <div className="mb-6 flex items-center justify-between z-10 relative">
          <div className="flex items-center space-x-2">
            <Activity className="text-red-600 h-5 w-5" />
            <h2 className="text-xl font-bold">
              Live Data Flow Architecture
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <span className="bg-green-900/30 text-green-400 text-xs px-3 py-1 rounded-full flex items-center border border-green-900/30">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
              <span>Real-time Monitoring</span>
            </span>
          </div>
        </div>

        {/* Workflow Visualization - Using a 5-column grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative mb-4">
          {/* SVG Connectors */}
          <svg className="absolute inset-0 w-full h-full z-0" style={{ pointerEvents: 'none' }}>
            {/* Lines from Endpoints to AlertStrike Workers */}
            <path 
              d="M290,120 C350,120 350,300 410,300" 
              fill="none" 
              stroke={isActiveFlow(0) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,200 C350,200 350,320 410,320" 
              fill="none" 
              stroke={isActiveFlow(1) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,280 C350,280 350,340 410,340" 
              fill="none" 
              stroke={isActiveFlow(2) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M290,360 C350,360 350,360 410,360" 
              fill="none" 
              stroke={isActiveFlow(3) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Workers to Master */}
            <path 
              d="M530,240 C550,240 550,240 570,240" 
              fill="none" 
              stroke={isActiveFlow(4) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,280 C550,280 550,255 570,255" 
              fill="none" 
              stroke={isActiveFlow(5) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,320 C550,320 550,270 570,270" 
              fill="none" 
              stroke={isActiveFlow(6) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,360 C550,360 550,285 570,285" 
              fill="none" 
              stroke={isActiveFlow(7) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M530,400 C550,400 550,300 570,300" 
              fill="none" 
              stroke={isActiveFlow(8) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Master to Indexers */}
            <path 
              d="M690,240 C720,240 720,200 750,200" 
              fill="none" 
              stroke={isActiveFlow(9) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M690,260 C720,260 720,260 750,260" 
              fill="none" 
              stroke={isActiveFlow(10) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M690,280 C720,280 720,320 750,320" 
              fill="none" 
              stroke={isActiveFlow(11) ? "rgba(239, 68, 68, 0.8)" : "rgba(239, 68, 68, 0.3)"} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Lines from Indexers to Dashboard */}
            <path 
              d="M870,200 C900,200 900,260 930,260" 
              fill="none" 
              stroke="rgba(239, 68, 68, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M870,260 C900,260 900,260 930,260" 
              fill="none" 
              stroke="rgba(239, 68, 68, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />
            <path 
              d="M870,320 C900,320 900,260 930,260" 
              fill="none" 
              stroke="rgba(239, 68, 68, 0.3)" 
              strokeWidth="2" 
              strokeDasharray="5,5" 
              className="line-path"
            />

            {/* Data Particles */}
            {isActiveFlow(0) && (
              <circle cx="350" cy="120" r="3" fill="#ef4444" className="data-particle">
                <animateMotion
                  path="M0,0 C60,0 60,180 120,180"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
            {isActiveFlow(5) && (
              <circle cx="540" cy="280" r="3" fill="#ef4444" className="data-particle">
                <animateMotion
                  path="M0,0 C20,0 20,-25 40,-25"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
            {isActiveFlow(10) && (
              <circle cx="710" cy="260" r="3" fill="#ef4444" className="data-particle">
                <animateMotion
                  path="M0,0 C30,0 30,0 60,0"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
          </svg>

          {/* Column 1: Endpoints */}
          <div className="md:col-span-1">
            <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-red-500" />
                  Endpoints
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Connected</span>
                </span>
              </div>

              <div className="space-y-4">
                {nodes.endpoints.map((endpoint, index) => (
                  <motion.div 
                    key={endpoint.id}
                    className="bg-black/40 p-3 rounded-lg border border-slate-800/80 hover:border-red-900/30 transition-colors group"
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-slate-900 rounded-lg border border-slate-800">
                        {endpoint.type === 'Server' && <Server className="h-5 w-5 text-blue-400" />}
                        {endpoint.type === 'Desktop' && <Monitor className="h-5 w-5 text-green-400" />}
                        {endpoint.type === 'Laptop' && <Laptop className="h-5 w-5 text-purple-400" />}
                        {endpoint.type === 'Virtual Machine' && <HardDrive className="h-5 w-5 text-yellow-400" />}
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{endpoint.type}</h4>
                        <div className="text-xs text-slate-400">{endpoint.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          <span className="text-green-400 flex items-center">
                            <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                            Connected
                          </span>
                          <span className="mx-1.5">•</span>
                          <span className="text-blue-400">{endpoint.logCount.toLocaleString()} logs</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full"
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ 
                          repeat: Infinity, 
                          duration: 2 + index, 
                          ease: "easeInOut" 
                        }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
                
                <motion.div 
                  className="flex justify-center items-center py-2 rounded-md bg-black/20 border border-slate-800 text-slate-400 text-sm hover:bg-black/40 hover:text-white transition-colors cursor-pointer"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <span className="flex items-center">
                    <span>View All {stats.totalEndpointsCapacity.toLocaleString()} Endpoints Capacity</span>
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
          
          {/* Column 2: AlertStrike Workers */}
          <div className="md:col-span-1">
            <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Server className="h-4 w-4 mr-2 text-red-500" />
                  Worker Nodes
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>

              <div className="space-y-3">
                {nodes.alertstrikeWorkers.map((worker, index) => (
                  <motion.div 
                    key={worker.id}
                    className={`bg-black/40 p-3 rounded-lg border ${worker.status === 'high-load' ? 'border-yellow-900/30' : 'border-slate-800/80'} hover:border-red-900/30 transition-colors`}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 ${worker.status === 'high-load' ? 'bg-yellow-950/20' : 'bg-slate-900'} rounded-lg border ${worker.status === 'high-load' ? 'border-yellow-900/30' : 'border-slate-800'}`}>
                        <Server className={`h-5 w-5 ${worker.status === 'high-load' ? 'text-yellow-500' : 'text-red-500'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-sm">{worker.name}</h4>
                        <div className="text-xs text-slate-400">{worker.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          {worker.status === 'operational' ? (
                            <span className="text-green-400 flex items-center">
                              <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                              Operational
                            </span>
                          ) : (
                            <span className="text-yellow-400 flex items-center">
                              <span className="h-1 w-1 bg-yellow-500 rounded-full mr-1 animate-pulse"></span>
                              High Load
                            </span>
                          )}
                          <span className="mx-1.5">•</span>
                          <span className={`${worker.status === 'high-load' ? 'text-yellow-400' : 'text-blue-400'}`}>{worker.load}% load</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${worker.status === 'high-load' ? 'bg-gradient-to-r from-yellow-900 via-yellow-600 to-yellow-700' : 'bg-gradient-to-r from-green-900 via-green-600 to-green-700'}`}
                        style={{ width: `${worker.load}%` }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Column 3: Master Node */}
          <div className="md:col-span-1">
            <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Cpu className="h-4 w-4 mr-2 text-red-500" />
                  AlertStrike AI Engine
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Core</span>
                </span>
              </div>

              <motion.div 
                className="bg-red-950/10 p-4 rounded-lg border border-red-900/30 hover:border-red-700/50 transition-colors relative overflow-hidden"
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                {/* Central processing animation */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-900/0 via-red-700/10 to-red-900/0 animate-pulse"></div>
                
                <div className="flex justify-center items-center mb-4">
                  <div className="p-3 bg-black/60 rounded-full border border-red-900/50 shadow-lg shadow-red-900/20">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 10, 
                        ease: "linear" 
                      }}
                    >
                      <Cpu className="h-10 w-10 text-red-500" />
                    </motion.div>
                  </div>
                </div>
                
                <div className="text-center mb-3">
                  <h4 className="font-bold text-white text-lg">{nodes.master.name}</h4>
                  <div className="text-sm text-red-400">{nodes.master.ip}</div>
                  
                  <div className="flex justify-center items-center space-x-2 mt-2">
                    <span className="text-green-400 text-sm flex items-center">
                      <span className="h-1.5 w-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                      Operational
                    </span>
                    <span className="text-slate-500">|</span>
                    <span className="text-red-400 text-sm">{nodes.master.load}% Load</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-black/50 p-2 rounded border border-red-900/20 text-center">
                    <div className="text-xs text-slate-400">Data Processing</div>
                    <div className="text-sm font-bold text-red-400">{nodes.master.dataRate}</div>
                  </div>
                  <div className="bg-black/50 p-2 rounded border border-red-900/20 text-center">
                    <div className="text-xs text-slate-400">Uptime</div>
                    <div className="text-sm font-bold text-green-400">99.99%</div>
                  </div>
                </div>
                
                <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-red-900 via-red-600 to-red-900 rounded-full relative"
                    initial={{ width: '0%' }}
                    animate={{ width: '90%' }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 3, 
                      ease: "easeInOut", 
                      repeatType: "reverse" 
                    }}
                  >
                    <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                  </motion.div>
                </div>
                
                <div className="mt-3 text-xs text-center text-slate-500">
                  Core Orchestration Engine
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Column 4: Indexers */}
          <div className="md:col-span-1">
            <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Database className="h-4 w-4 mr-2 text-red-500" />
                  Indexers
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Storage</span>
                </span>
              </div>

              <div className="space-y-3">
                {nodes.indexers.map((indexer, index) => (
                  <motion.div 
                    key={indexer.id}
                    className="bg-black/40 p-3 rounded-lg border border-slate-800/80 hover:border-red-900/30 transition-colors"
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-950/20 rounded-lg border border-blue-900/30">
                        <Database className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-sm">{indexer.name}</h4>
                        <div className="text-xs text-slate-400">{indexer.ip}</div>
                        <div className="flex items-center mt-1 text-xs">
                          <span className="text-green-400 flex items-center">
                            <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                            Operational
                          </span>
                          <span className="mx-1.5">•</span>
                          <span className="text-blue-400">{indexer.indexRate}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-blue-900 via-blue-600 to-blue-900 rounded-full"
                        animate={{ 
                          width: ['60%', '80%', '70%', '90%', '75%'] 
                        }}
                        transition={{ 
                          repeat: Infinity, 
                          duration: 5, 
                          times: [0, 0.2, 0.5, 0.8, 1] 
                        }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Column 5: Dashboard & Users */}
          <div className="md:col-span-1">
            <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm mb-6 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Activity className="h-4 w-4 mr-2 text-red-500" />
                  Dashboard
                </h3>
                <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full flex items-center border border-green-900/30">
                  <span className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  <span>Online</span>
                </span>
              </div>

              <motion.div 
                className="bg-slate-900/60 p-4 rounded-lg border border-slate-800 hover:border-red-900/30 transition-colors mb-4"
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex items-start space-x-3 mb-3">
                  <div className="p-2 bg-purple-950/20 rounded-lg border border-purple-900/30">
                    <Activity className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{nodes.dashboard.name}</h4>
                    <div className="text-xs text-slate-400">{nodes.dashboard.ip}</div>
                    <div className="flex items-center mt-1 text-xs">
                      <span className="text-green-400 flex items-center">
                        <span className="h-1 w-1 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                        Operational
                      </span>
                      <span className="mx-1.5">•</span>
                      <span className="text-purple-400">{nodes.dashboard.activeUsers} active users</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-black/40 p-2 rounded border border-slate-800 text-center">
                    <div className="text-xs text-slate-400">Queries</div>
                    <div className="text-sm font-bold text-blue-400">283/min</div>
                  </div>
                  <div className="bg-black/40 p-2 rounded border border-slate-800 text-center">
                    <div className="text-xs text-slate-400">Response</div>
                    <div className="text-sm font-bold text-green-400">124ms</div>
                  </div>
                  <div className="bg-black/40 p-2 rounded border border-slate-800 text-center">
                    <div className="text-xs text-slate-400">Load</div>
                    <div className="text-sm font-bold text-green-400">32%</div>
                  </div>
                </div>
                
                <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-purple-900 via-purple-600 to-purple-900 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: '90%' }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 4, 
                      ease: "easeInOut", 
                      repeatType: "reverse" 
                    }}
                  ></motion.div>
                </div>
              </motion.div>

              <motion.div 
                className="bg-slate-900/60 p-4 rounded-lg border border-slate-800 hover:border-red-900/30 transition-colors"
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-white flex items-center">
                    <UserCircle className="h-4 w-4 mr-2 text-blue-400" />
                    ShieldX Analysts
                  </h4>
                  <span className="bg-blue-900/30 text-blue-400 text-xs px-2 py-0.5 rounded-full border border-blue-900/30">
                    {nodes.dashboard.activeUsers} Online
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {[...Array(4)].map((_, index) => (
                    <motion.div 
                      key={index}
                      className="bg-black/40 p-2 rounded-lg border border-slate-800 flex flex-col items-center text-center"
                      whileHover={{ y: -2, transition: { duration: 0.2 } }}
                      animate={{ 
                        y: [0, -3, 0],
                      }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 2 + index * 0.5, 
                        delay: index * 0.2,
                        ease: "easeInOut" 
                      }}
                    >
                      <div className="h-8 w-8 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full border border-slate-700 mb-1 flex items-center justify-center">
                        <UserCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="text-xs text-slate-400">Analyst</div>
                      <div className="text-xs mt-1">
                        <span className="inline-block h-1.5 w-1.5 bg-green-500 rounded-full mr-1"></span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-3 flex justify-center">
                  <span className="text-xs text-slate-500 flex items-center">
                    <Lock className="h-3 w-3 mr-1 text-green-500" />
                    Secure Access Protocol
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* System Performance Indicators */}
        <div className="bg-black/40 border border-slate-800 rounded-lg p-4 backdrop-blur-sm">
          <h3 className="font-bold mb-4 flex items-center text-sm">
            <AlertTriangle className="h-4 w-4 mr-2 text-yellow-500" />
            System Performance Metrics
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">CPU Load</div>
              <div className="text-xl font-bold text-blue-400">{stats.cpuLoad.toFixed(1)}%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-700 rounded-full" style={{ width: `${stats.cpuLoad}%` }}></div>
              </div>
            </div>
            
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">Memory</div>
              <div className="text-xl font-bold text-purple-400">62.3%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-purple-700 rounded-full" style={{ width: '62.3%' }}></div>
              </div>
            </div>
            
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">Disk Usage</div>
              <div className="text-xl font-bold text-green-400">{stats.diskUsage.toFixed(1)}%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-green-700 rounded-full" style={{ width: `${stats.diskUsage}%` }}></div>
              </div>
            </div>
            
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">Network</div>
              <div className="text-xl font-bold text-yellow-400">421 MB/s</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-yellow-700 rounded-full"
                  animate={{ width: ['40%', '70%', '50%', '90%', '60%'] }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 5,
                    times: [0, 0.25, 0.5, 0.75, 1] 
                  }}
                ></motion.div>
              </div>
            </div>
            
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">Response Time</div>
              <div className="text-xl font-bold text-red-400">124 ms</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-700 to-green-500 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            
            <div className="bg-black/60 p-3 rounded-lg border border-slate-800">
              <div className="text-xs text-slate-400 mb-1">Security Score</div>
              <div className="text-xl font-bold text-green-400">98%</div>
              <div className="mt-2 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-700 to-green-500 rounded-full" style={{ width: '98%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DataWorkflow;
